import useSWR, { mutate } from "swr";
import { getAuctionItemFilterSettings, getAuctionItems } from "@/actions/auctions";
import { REVALIDATE_BID_TIME, EXTRA_BID_TIME, REVALIDATE_FILTERS_TIME } from "@/lib/auctions";
import { getRecentItemBidsByAuctionPartId } from "@/actions/bids";

const getPageForSearchValue = (
    searchValue: number,
    paginationArgs: any
): number => {
    const { startItemSortNo = 1, size, total } = paginationArgs;

    if (
        searchValue < startItemSortNo ||
        searchValue > startItemSortNo + total - 1
    ) {
        return -1;
    }

    return Math.ceil((searchValue - startItemSortNo + 1) / size);
};

import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { auctionFilterProps, AuctionItem } from "@/types/auction";
import useDebounce from "./useDebounce";
import useBidStore from "@/stores/useBidStore";
import { usePathname } from "next/navigation";
import { parseDate } from "@/lib/utils";

interface PaginationArgs {
    startItemSortNo: number;
    size: number;
    total: number;
    page: number;
}

const useAuctionItemData = (
    auctionPartId: number,
    filterSelections: any,
    serverTime: any,
    paginationArgs: PaginationArgs,
    auctionItemSearchValue: number | null,
    updatedFirstAvailablePage: any,
    filteringSettingsServer: any,
    pageQuery: any,
    currentPageMemo: number,
    auctionItemBids: any,
    auction: any,
    buildFilterParams: () => any,
    setOptions: Dispatch<SetStateAction<any>>,
    setVisibleItemsData: Dispatch<SetStateAction<number | boolean>>,
    setFilteredAuctionItems: Dispatch<SetStateAction<any[]>>,
    setAuctionItemBids: Dispatch<SetStateAction<any[]>>,
    setUpdatedFirstAvailablePage: Dispatch<SetStateAction<number>>,
) => {
    const { resetBidFetchSeed, bidFetchKeySeed, setBidFetchKeySeed } = useBidStore();
    const pathname = usePathname();

    const [skipFirstStaleFetch, setSkipFirstStaleFetch] = useState(false);
    const [hasFetchedFull, setHasFetchedFull] = useState(false);
    const [fastRefresh, setFastRefresh] = useState(true);

    // For recently ended auctions (< 48h), use a unique seed to ensure fresh bids on first load
    const [endedFreshSeed] = useState(() => {
        if (auction?.partStatus === "ended" && auction?.itemLastEndDate) {
            const lastEndTime = parseDate(auction?.itemLastEndDate);
            const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
            if (Date.now() - lastEndTime < twoDaysMs) {
                return Date.now().toString();
            }
        }
        return "";
    });

    // Ref to always call the latest fetcher with the latest key
    const pollRef = useRef(() => auctionDataFetcher(auctionDataFetcherKey));
    useEffect(() => { pollRef.current = () => auctionDataFetcher(auctionDataFetcherKey); });

    const auctionHasEnded = auction?.partStatus === "ended";

    const hasServerTime = !!serverTime;

    const selectedStatusFilters = filterSelections?.filter(
        (selection: any) => selection?.matchKey === "itemEndTime"
    );

    const hasActiveFilter = filterSelections?.some(
        (selection: any) =>
            selection?.matchKey === "itemEndTime" && selection?.value === "active"
    );

    const hasEndedFilter = filterSelections?.some(
        (selection: any) =>
            selection?.matchKey === "itemEndTime" && selection?.value === "ended"
    );

    const hideEndedItems = hasActiveFilter && selectedStatusFilters?.length === 1;
    const hideActiveItems = hasEndedFilter && selectedStatusFilters?.length === 1;

    const auctionDataFetcherKey = useMemo(
        () => [
            "api/getFilteredAuctionItemsAndBids",
            auctionPartId,
            currentPageMemo,
            hasServerTime,
            filterSelections,
            bidFetchKeySeed
        ],
        [auctionPartId, currentPageMemo, filterSelections, hasServerTime, bidFetchKeySeed]
    );

    const filterSettingsFetcherKey = ["api/fetchAuctionFilterSettings", auctionPartId]

    const debounceAuctionItemNoSearchValue = useDebounce(
        auctionItemSearchValue,
        500
    );

    // Fix back and forth
    useEffect(() => {
        mutate(auctionDataFetcherKey, undefined, { revalidate: true });
        mutate(filterSettingsFetcherKey, undefined, { revalidate: true });
    }, [pathname]);

    // Don't automatically reset bidFetchKeySeed - let it persist
    // Resetting causes SWR to revert to old cached data
    // useEffect(() => {
    //     if (!bidFetchKeySeed) return;

    //     const timer = setTimeout(() => {
    //         setSkipFirstStaleFetch(true);
    //         resetBidFetchSeed();
    //     }, 5000);

    //     return () => clearTimeout(timer);
    // }, [bidFetchKeySeed]);

    // Go to page when search is triggered
    useEffect(() => {
        if (updatedFirstAvailablePage === false) return;

        setFastRefresh(true);
        mutate(auctionDataFetcherKey, undefined, { revalidate: true });

        // Scroll to top
        window.scrollTo(0, 0);
    }, [debounceAuctionItemNoSearchValue]);

    // Go to page when pagination is triggered
    useEffect(() => {
        if (updatedFirstAvailablePage === false) return;

        setFastRefresh(true);
        mutate(auctionDataFetcherKey, undefined, { revalidate: true });

        // Scroll to top
        window.scrollTo(0, 0);
    }, [currentPageMemo, updatedFirstAvailablePage]);

    const auctionDataFetcher = async (key: any[]) => {
        const filter = buildFilterParams() as any;

        // Extract the page number from the cache key to avoid stale closure reads
        const [, , pageFromKey] = key;
        const currentPage = pageFromKey || paginationArgs.page;

        const offset = (paginationArgs?.startItemSortNo - 1) + (currentPage - 1) * paginationArgs.size;

        const params = {
            offset,
            limit: paginationArgs.size,
        };

        let auctionItemsData = null;

        try {
            auctionItemsData = await getAuctionItems(
                auctionPartId,
                !auctionItemSearchValue ? params : {},
                null,
                auctionItemSearchValue || null
            );

            const getAuctionItemsResponse = auctionItemsData?.auctionItems || [];

            const auctionItemIds =
                getAuctionItemsResponse?.map((item: any) => item.itemId) || [];

            let bidsData =
                auctionItemBids?.length > 0
                    ? {
                        allItemBids: auctionItemBids,
                    }
                    : null;

            // Use a fresh seed for recently ended auctions (< 24h) to bypass any stale cached data
            const fetchSeed = (auctionHasEnded && endedFreshSeed) ? endedFreshSeed : bidFetchKeySeed;

            if (fastRefresh) {
                setFastRefresh(false);

                await getRecentItemBidsByAuctionPartId(auction?.partId, auctionItemIds, fetchSeed).then(
                    (data) => {
                        setAuctionItemBids(data?.allItemBids || []);
                        bidsData = data;
                    }
                );
            } else {
                bidsData = await getRecentItemBidsByAuctionPartId(
                    auction?.partId,
                    auctionItemIds,
                    fetchSeed
                );
            }

            // Skip the first stale fetch of bids and auction has not ended
            if (skipFirstStaleFetch && !auctionHasEnded && serverTime) {
                setSkipFirstStaleFetch(false);
                return;
            }

            const allItemBids = bidsData?.allItemBids || [];

            const allAuctionItems = getAuctionItemsResponse?.map((item: any) => {
                const bidMatch = allItemBids?.find(
                    (bid: any) => bid.itemId === item.itemId
                );

                let parseItemEndTime = item.itemEndTime ? parseDate(item.itemEndTime) : 0;
                let parseBidMatchItemEndTime = bidMatch?.itemEndTime ? parseDate(bidMatch?.itemEndTime) : 0;

                const updatedItemEndTime = (bidMatch?.itemEndTime && parseBidMatchItemEndTime > parseItemEndTime) ? bidMatch?.itemEndTime : item.itemEndTime
                const itemEndTimestamp = parseDate(updatedItemEndTime)

                const hasEnded =
                    !serverTime ||
                    itemEndTimestamp + EXTRA_BID_TIME < parseDate(serverTime)

                const isHidden = () => {
                    const freightHelpFilter = filter?.freightHelp;
                    const forkLiftHelpFilter = filter?.forkLiftHelp;

                    // Item has ended, should be hidden if hideEndedItems is true
                    if (hasEnded && hideEndedItems) return true;

                    // Item is active, should be hidden if hideActiveItems is true
                    if (!hasEnded && hideActiveItems) return true;

                    // Filter out if its no match on itemSortNo and  debouncedAuctionItemNoSearchValue
                    if (
                        auctionItemSearchValue &&
                        item.itemSortNo !== Number(auctionItemSearchValue)
                    )
                        return true;

                    // Filter out items that are not in the selected freight help filter
                    if (
                        forkLiftHelpFilter &&
                        forkLiftHelpFilter.length > 0 &&
                        !forkLiftHelpFilter.includes(item.itemForkliftHelp)
                    )
                        return true;
                    if (
                        freightHelpFilter &&
                        freightHelpFilter.length > 0 &&
                        !freightHelpFilter.includes(item.itemFreightHelp)
                    )
                        return true;

                    return false;
                };

                return {
                    ...item,
                    itemEndTime: updatedItemEndTime,
                    itemHasEnded: hasEnded,
                    isHidden: isHidden(),
                };
            });

            const actualVisibleItems = !serverTime
                ? false
                : allAuctionItems.filter((item: any) => !item.isHidden)?.length;

            const hasSearched = auctionItemSearchValue;

            const searchPage = hasSearched
                ? getPageForSearchValue(Number(auctionItemSearchValue), paginationArgs)
                : null;

            const newPage = hasSearched ? searchPage : paginationArgs.page;

            if (
                newPage !== paginationArgs.page ||
                filteringSettingsServer?.totalItems !== paginationArgs.total
            ) {
                setOptions((prevArgs: auctionFilterProps) => ({
                    ...prevArgs,
                    pagination: {
                        ...prevArgs.pagination,
                        total: filteringSettingsServer?.totalItems || 0,
                        ...(hasSearched ? { page: newPage } : {}),
                    },
                }));
            }

            // Cache the successful data
            setVisibleItemsData(actualVisibleItems);
            setFilteredAuctionItems(allAuctionItems);
            setAuctionItemBids(allItemBids);

            setHasFetchedFull(true);
            //console.log("[SWR] TRIGGERING FETCH", "SUCCESS");

            return {
                auctionItems: allAuctionItems,
                allItemBids,
                serverTime,
            }
        } catch (error: any) {
            return {
                auctionItems: [],
                allItemBids: [],
                serverTime: null
            }
        }
    };

    const { data, error, isLoading } = useSWR(auctionDataFetcherKey, auctionDataFetcher, {
        refreshInterval: auctionHasEnded ? 0 : REVALIDATE_BID_TIME,
        revalidateOnFocus: !(auctionHasEnded && endedFreshSeed),
        revalidateOnReconnect: !(auctionHasEnded && endedFreshSeed),
        shouldRetryOnError: true,
    });

    // Failsafe extra polling in case SWR refreshInterval is suppressed (e.g. Safari background tabs)
    useEffect(() => {
        if (auctionHasEnded) return;
        
        const id = setInterval(() => {
            pollRef.current()
        }, 10000);

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                pollRef.current()
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [auctionHasEnded]);

    useSWR<AuctionItem[], Error>(
        filterSettingsFetcherKey,
        async () => {
            const filteringSettings = await getAuctionItemFilterSettings(
                Number(auctionPartId)
            );

            setOptions((prevOptions: any) => ({
                ...prevOptions,
                filtering: {
                    ...prevOptions.filtering,
                    options: filteringSettings?.options,
                },
                pagination: {
                    ...prevOptions.pagination,
                    startItemSortNo: filteringSettings?.startItemSortNo,
                    lastClosedSortNo: filteringSettings?.lastClosedSortNo,
                    allAvailablePages: filteringSettings?.allAvailablePages,
                    firstAvailablePage: filteringSettings?.firstAvailablePage,
                    totalItems: filteringSettings?.totalItems,
                    page:
                        !updatedFirstAvailablePage && !pageQuery && !auctionHasEnded
                            ? filteringSettings?.firstAvailablePage || 1
                            : prevOptions.pagination.page,
                    hasLoadedPages: true,
                },
            }));

            setUpdatedFirstAvailablePage(filteringSettings?.firstAvailablePage || 1);

            return filteringSettings;
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            refreshInterval: auctionHasEnded ? 0 : REVALIDATE_FILTERS_TIME,
        }
    );

    return { data, error, hasFetchedFull, isLoading, mutate };
};

export default useAuctionItemData;